import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowLeft, Play } from "lucide-react";
import { images, albumsData } from "../data";

export default function Galleri() {
  const [activeParentAlbum, setActiveParentAlbum] = useState(null);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleAlbumClick = (album) => {
    if (album.subAlbums) {
      setActiveParentAlbum(album);
      setActiveAlbum(null);
    } else {
      setActiveAlbum(album);
    }
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };
  
  const goBack = () => {
    if (activeAlbum && activeParentAlbum) {
      // If viewing images inside a sub-album, go back to parent album's sub-album list
      setActiveAlbum(null);
    } else {
      // Otherwise go back to main albums list
      setActiveAlbum(null);
      setActiveParentAlbum(null);
    }
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const openLightbox = (index) => setSelectedImage(index);
  const closeLightbox = () => setSelectedImage(null);
  
  const nextImage = (e) => {
    e.stopPropagation();
    if (!activeAlbum) return;
    setSelectedImage((prev) => (prev + 1) % activeAlbum.images.length);
  };
  
  const prevImage = (e) => {
    e.stopPropagation();
    if (!activeAlbum) return;
    setSelectedImage((prev) => (prev - 1 + activeAlbum.images.length) % activeAlbum.images.length);
  };

  const currentAlbums = activeParentAlbum ? activeParentAlbum.subAlbums : albumsData;

  return (
    <div className="bg-white min-h-screen pt-20 font-sans">
      
      {/* Hero Block */}
      <section 
        className="relative h-[40vh] md:h-[50vh] bg-cover bg-center"
        style={{ backgroundImage: `url(${images.subpageHero})` }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
             <span className="text-primary tracking-widest text-sm uppercase mb-4 block">Gå på opdagelse</span>
             <h1 className="text-4xl md:text-6xl text-white font-serif tracking-wide text-shadow-sm">Galleri</h1>
          </motion.div>
        </div>
      </section>

      {/* Album Section */}
      <section className="py-16 md:py-24 bg-white min-h-[60vh]">
         <div className="container mx-auto px-6 max-w-[1400px]">
            
            <AnimatePresence mode="wait">
              {!activeAlbum ? (
                <motion.div
                  key={activeParentAlbum ? activeParentAlbum.id : "main-grid"}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
                    <div>
                      {activeParentAlbum && (
                        <button 
                          onClick={goBack}
                          className="flex items-center text-xs tracking-widest uppercase text-gray-500 hover:text-primary transition-colors mb-4 group"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                          Tilbage til alle albummer
                        </button>
                      )}
                      <h2 className="text-3xl md:text-4xl font-serif">
                        {activeParentAlbum ? activeParentAlbum.title : "Vores Albummer"}
                      </h2>
                    </div>
                    {!activeParentAlbum && (
                      <p className="text-gray-400 italic text-sm tracking-widest uppercase">
                        Vælg en kategori
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                    {currentAlbums.map((album) => (
                      <motion.div
                        key={album.id}
                        whileHover={{ y: -5 }}
                        className="group cursor-pointer"
                        onClick={() => handleAlbumClick(album)}
                      >
                        <div className="relative aspect-[4/5] overflow-hidden bg-gray-100 mb-4">
                          {album.cover.endsWith('.mov') || album.cover.endsWith('.mp4') ? (
                            <video 
                              src={album.cover}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                              muted
                              playsInline
                            />
                          ) : (
                            <img 
                              src={album.cover} 
                              alt={album.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                          )}
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                             <span className="text-white text-xs tracking-[0.2em] uppercase border border-white/40 px-4 py-2">
                               {album.subAlbums ? "Se Under-albummer" : "Åbn Album"}
                             </span>
                          </div>
                        </div>
                        <h3 className="text-sm tracking-widest uppercase font-medium text-center">{album.title}</h3>
                        {album.subAlbums && (
                           <p className="text-[10px] text-gray-400 text-center uppercase tracking-widest mt-1">
                             {album.subAlbums.length} Kategorier
                           </p>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="album-detail"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
                    <div>
                      <button 
                        onClick={goBack}
                        className="flex items-center text-xs tracking-widest uppercase text-gray-500 hover:text-primary transition-colors mb-4 group"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                        Tilbage til {activeParentAlbum ? activeParentAlbum.title : "albummer"}
                      </button>
                      <h2 className="text-3xl md:text-4xl font-serif">{activeAlbum.title}</h2>
                    </div>
                    <div className="text-gray-400 text-sm tracking-widest uppercase">
                      {activeAlbum.images.length} Billeder
                    </div>
                  </div>

                  {activeAlbum.images.length > 0 ? (
                    <div className="columns-1 sm:columns-2 md:columns-3 xl:columns-6 gap-6 space-y-6">
                      {activeAlbum.images.map((img, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          className="relative group cursor-zoom-in overflow-hidden"
                          onClick={() => openLightbox(idx)}
                        >
                          {img.type === 'video' ? (
                            <video 
                              src={img.src}
                              className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                              muted
                              playsInline
                            />
                          ) : (
                            <img 
                              src={img.src} 
                              alt={img.alt}
                              className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                          )}
                          {img.type === 'video' && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="bg-black/50 p-3 rounded-full backdrop-blur-sm">
                                <Play className="w-6 h-6 text-white fill-white" />
                              </div>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-32 text-center border-2 border-dashed border-gray-100">
                      <p className="text-gray-400 italic">Dette album er i øjeblikket tomt. Billeder bliver tilføjet snart.</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
         </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage !== null && activeAlbum && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-10 right-10 text-white hover:text-primary transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            {activeAlbum.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 md:left-10 text-white hover:text-primary transition-colors"
                >
                  <ChevronLeft className="w-10 h-10" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 md:right-10 text-white hover:text-primary transition-colors"
                >
                  <ChevronRight className="w-10 h-10" />
                </button>
              </>
            )}

            {activeAlbum.images[selectedImage].type === 'video' ? (
              <video
                key={selectedImage}
                src={activeAlbum.images[selectedImage].src}
                className="max-w-full max-h-[80vh] object-contain shadow-2xl"
                controls
                autoPlay
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <motion.img
                key={selectedImage}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                src={activeAlbum.images[selectedImage].src}
                alt={activeAlbum.images[selectedImage].alt}
                className="max-w-full max-h-[80vh] object-contain shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            )}

            <div className="absolute bottom-10 text-white/60 text-sm tracking-widest uppercase">
              {selectedImage + 1} / {activeAlbum.images.length} — {activeAlbum.images[selectedImage].alt}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
